import { useCallback, useEffect, useRef, useState } from 'react';
import { newIdempotencyKey, type ApiClient } from '../api/client';
import type { JsonObject, Problem, StudioTrial } from '../api/types';
import { toProblem } from './useAsync';

export interface DraftTrialState {
  /** 帯に出している試運転。閉じたか、まだ 1 度も配備していなければ null。 */
  trial: StudioTrial | null;
  /** deployDraft の呼び出し中。 */
  deploying: boolean;
  /** 配備・開始・読み直しの失敗。 */
  problem: Problem | null;
  deploy(confirmReplace: boolean): Promise<void>;
  start(processKey: string, input: JsonObject): Promise<void>;
  starting: boolean;
  /** 帯を閉じる（終わった試運転にだけ意味がある）。次の配備で戻る。 */
  dismiss(): void;
  /**
   * 読み直す。SSE の studio.trial が持つ trialId を渡すと、その試運転を帯に出す（G30）。
   * 渡さなければ、帯に出している試運転（無ければいちばん新しい試運転）を読み直す。
   */
  reload(trialId?: string): void;
}

export interface DraftTrialOptions {
  api: ApiClient;
  workspaceId: string;
  appId: string | null;
  /** false か appId が null なら API を呼ばない（fixture モード、Draft が無いとき）。 */
  enabled: boolean;
  /** 画面の最初の状態（DraftReview.trial）。 */
  initial: StudioTrial | null;
  /** 配備の失敗・試運転の終わりで呼ぶ。レビューと Draft を取り直させる。 */
  onChanged?(): void;
}

/** 配備の進捗イベントが無いので、進んでいる間だけ読み直す（G5。UploadPanel と同じ間隔）。 */
const POLL_INTERVAL_MS = 1_000;
/** 読み直しが失敗したときの待ち（失敗のたびに倍にし、この上限で止める）。 */
const POLL_BACKOFF_MAX_MS = 8_000;

function isPolling(status: string | null): boolean {
  return status === 'deploying' || status === 'running' || status === 'needs_attention';
}

/**
 * 「試す」→ 配備 → 試運転の 1 本の状態（計画の S5 G5・G27）。
 * 配備は 202 で受け付けるだけなので、受付の trialId を読み直して帯の元にする。
 */
export function useDraftTrial({ api, workspaceId, appId, enabled, initial, onChanged }: DraftTrialOptions): DraftTrialState {
  const [trial, setTrial] = useState<StudioTrial | null>(initial);
  const [deploying, setDeploying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  // reload() の要求。trialId を渡されたらその試運転を読む（A2）。
  const [request, setRequest] = useState<{ token: number; trialId: string | null }>({ token: 0, trialId: null });
  // 帯の元にした試運転（DraftReview.trial か、自分で読んだもの）。
  const [shown, setShown] = useState<{ appId: string | null; id: string | null }>({
    appId,
    id: initial?.id ?? null,
  });
  // 呼び出し側の関数と今の試運転は、useCallback の依存に入れずに参照する。
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const current = useRef<StudioTrial | null>(trial);
  current.current = trial;
  // 利用者が閉じた試運転（A14）。対象を指定しない読み直しでは開き直さない。
  const dismissed = useRef<string | null>(null);
  // 帯の世代。「試す」と「試運転を始める」で進み、それより前に始まった読み直しの結果は捨てる（T16-1）。
  const generation = useRef(0);

  // 描画中に合わせる（effect だと前の app の帯が 1 度描かれる。DraftHistory と同じ作り）。
  if (shown.appId !== appId) {
    setShown({ appId, id: initial?.id ?? null });
    setTrial(initial);
    setProblem(null);
    dismissed.current = null;
  } else if (trial === null && initial !== null && initial.id !== shown.id) {
    // 画面を開いた直後にレビューが届いたときだけ、続きの試運転を帯に出す。
    // 閉じた帯（dismiss）は id が同じなので開き直さない。
    setShown({ appId, id: initial.id });
    setTrial(initial);
  }

  const adopt = useCallback(
    (next: StudioTrial) => {
      generation.current += 1;
      dismissed.current = null;
      setTrial(next);
      setShown({ appId, id: next.id });
    },
    [appId],
  );

  const trialId = trial?.id ?? null;
  const polling = enabled && appId !== null && trialId !== null && isPolling(trial?.status ?? null);

  useEffect(() => {
    if (!polling || appId === null || trialId === null) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let wait = POLL_INTERVAL_MS;

    const poll = async () => {
      const generationAtRead = generation.current;
      try {
        const next = await api.getTrial(workspaceId, appId, trialId);
        if (!active || generation.current !== generationAtRead) return;
        setTrial(next);
        setProblem(null);
        wait = POLL_INTERVAL_MS;
        if (!isPolling(next.status)) {
          // 段階の条件・状態チップ・「試す」の可否が変わる。
          changed.current?.();
          return;
        }
      } catch (error) {
        if (!active) return;
        // 1 回の失敗で読み直しを止めると帯が「配備中」のまま固まるので、待ってから続ける（A4）。
        setProblem(toProblem(error));
        wait = Math.min(wait * 2, POLL_BACKOFF_MAX_MS);
      }
      timer = setTimeout(() => void poll(), wait);
    };
    timer = setTimeout(() => void poll(), wait);

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
    // request はポーリングを起こし直すためだけの依存（「再取得」で待ちを最初からにする。A4）。
  }, [api, workspaceId, appId, trialId, polling, request]);

  // reload()。対象を指定されればその試運転、帯に何も無ければいちばん新しい試運転を拾う（AI の trial_deploy）。
  useEffect(() => {
    if (request.token === 0 || !enabled || appId === null) return;
    let active = true;
    const load = async () => {
      const generationAtRead = generation.current;
      const target = request.trialId ?? current.current?.id ?? null;
      try {
        const next =
          target === null
            ? ((await api.listTrials(workspaceId, appId, { limit: 1 })).items[0] ?? null)
            : await api.getTrial(workspaceId, appId, target);
        if (!active || next === null) return;
        // 「試す」で新しい試運転を帯に出した後に古い読み直しが返っても上書きしない（T16-1）。
        if (generation.current !== generationAtRead) return;
        // 名指しの読み直しでも、帯に出している試運転より古いものなら捨てる。世代カウンタが捨てるのは
        // 「読んでいる最中に adopt が入った」ときだけで、adopt の後に届いた古い studio.trial
        // （Agent の trial_get が古い試運転を読んで出した finished など）はここで止める。
        const showing = current.current;
        if (showing !== null && showing.id !== next.id && next.createdAt < showing.createdAt) return;
        // 利用者が閉じた帯は、対象を名指しされたときだけ開き直す（A14）。
        if (request.trialId === null && dismissed.current === next.id) return;
        if (request.trialId !== null) dismissed.current = null;
        setTrial(next);
        setShown({ appId, id: next.id });
      } catch (error) {
        if (active) setProblem(toProblem(error));
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [api, workspaceId, appId, enabled, request]);

  const deploy = useCallback(
    async (confirmReplace: boolean) => {
      if (!enabled || appId === null) return;
      setDeploying(true);
      setProblem(null);
      try {
        // 冪等キーは押すたびに 1 つ（同じ head でも「試す」は配備し直せる。G20）。
        const accepted = await api.deployDraft(workspaceId, appId, { confirmReplace }, { idempotencyKey: newIdempotencyKey() });
        // 202 の本文は upload だけなので、帯の元になる試運転を読む。
        adopt(await api.getTrial(workspaceId, appId, accepted.trialId));
        changed.current?.();
      } catch (error) {
        setProblem(toProblem(error));
      } finally {
        setDeploying(false);
      }
    },
    [api, workspaceId, appId, enabled, adopt],
  );

  const start = useCallback(
    async (processKey: string, input: JsonObject) => {
      const showing = current.current;
      if (!enabled || appId === null || showing === null) return;
      setStarting(true);
      setProblem(null);
      try {
        const started = await api.startTrial(
          workspaceId,
          appId,
          showing.id,
          { processKey, input },
          { idempotencyKey: newIdempotencyKey() },
        );
        adopt(started.trial);
        changed.current?.();
      } catch (error) {
        setProblem(toProblem(error));
      } finally {
        setStarting(false);
      }
    },
    [api, workspaceId, appId, enabled, adopt],
  );

  const dismiss = useCallback(() => {
    dismissed.current = current.current?.id ?? null;
    setTrial(null);
    setProblem(null);
  }, []);

  const reload = useCallback((trialId?: string) => {
    setRequest((value) => ({ token: value.token + 1, trialId: trialId ?? null }));
  }, []);

  return { trial, deploying, problem, deploy, start, starting, dismiss, reload };
}
